import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { UserRole } from '../types'
import LoadingSpinner from './LoadingSpinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: UserRole[]
  fallback?: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  fallback 
}) => {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Check role-based access if required
  if (requiredRole && user) {
    const userRole = user.role
    const hasRequiredRole = requiredRole.includes(userRole)
    
    if (!hasRequiredRole) {
      // Show fallback component or access denied page
      if (fallback) {
        return <>{fallback}</>
      }
      
      return (
        <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-dark-900 rounded-lg border border-dark-700 p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m9-6V9a3 3 0 00-3-3H6a3 3 0 00-3 3v3" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-white mb-2">Access Denied</h1>
              <p className="text-dark-400 mb-6">
                You don't have permission to access this page. Contact your administrator if you believe this is an error.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => window.history.back()}
                  className="w-full btn btn-outline"
                >
                  Go Back
                </button>
                <a
                  href="/dashboard"
                  className="w-full btn btn-primary"
                >
                  Go to Dashboard
                </a>
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  // Render children if all checks pass
  return <>{children}</>
}

export default ProtectedRoute